/**
 * Resilient fetch utility — adds timeout and retry with exponential backoff.
 *
 * Drop-in replacement for native fetch() in hooks/stores.
 * On slow networks, native fetch hangs until the TCP stack gives up (60-120s)
 * then throws TypeError: "Failed to fetch" with no retry.
 */

interface ResilientFetchOptions extends RequestInit {
  /** Timeout in ms. Default: 15_000 for GET, 30_000 for mutations. */
  timeout?: number;
  /** Max retry attempts for transient failures. Default: 2 for GET, 0 for mutations. */
  retries?: number;
  /** Base delay in ms for exponential backoff. Default: 1000. */
  retryDelay?: number;
}

/** Errors that are worth retrying — network failures, not 4xx client errors. */
function isRetryable(error: unknown, response?: Response): boolean {
  // Network-level failure (TypeError: Failed to fetch)
  if (error instanceof TypeError) return true;
  // AbortError from our timeout — network is too slow, worth retrying
  if (error instanceof DOMException && error.name === "AbortError") return true;
  // Server errors are retryable, client errors are not
  if (response && response.status >= 500) return true;
  return false;
}

export async function resilientFetch(
  url: string,
  options: ResilientFetchOptions = {}
): Promise<Response> {
  const method = (options.method || "GET").toUpperCase();
  const isRead = method === "GET" || method === "HEAD";

  const timeout = options.timeout ?? (isRead ? 15_000 : 30_000);
  const maxRetries = options.retries ?? (isRead ? 2 : 0);
  const baseDelay = options.retryDelay ?? 1_000;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);

    // Chain with caller's signal if provided
    if (options.signal) {
      options.signal.addEventListener("abort", () => controller.abort(), {
        once: true,
      });
    }

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timer);

      // Don't retry client errors (4xx)
      if (!response.ok && response.status < 500) {
        return response;
      }

      // Retry server errors
      if (!response.ok && isRetryable(null, response) && attempt < maxRetries) {
        lastError = new Error(`Server error: ${response.status}`);
        await sleep(baseDelay * Math.pow(2, attempt));
        continue;
      }

      return response;
    } catch (err) {
      clearTimeout(timer);

      // If caller aborted, don't retry
      if (options.signal?.aborted) throw err;

      lastError = err;

      if (attempt < maxRetries && isRetryable(err)) {
        await sleep(baseDelay * Math.pow(2, attempt));
        continue;
      }

      // Final attempt — throw with a better message
      if (err instanceof TypeError && err.message === "Failed to fetch") {
        throw new NetworkError(
          "Network unavailable — check your connection and try again"
        );
      }
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new NetworkError("Request timed out — connection is too slow");
      }
      throw err;
    }
  }

  // Should not reach here, but satisfy TS
  throw lastError;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Distinct error class so callers can detect network issues. */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkError";
  }
}
